import { supabase } from '../../config/database';
// Import dynamique de sharp uniquement côté serveur
let sharp: any;
if (typeof window === 'undefined') {
  sharp = require('../../utils/sharpWrapper').default;
}
import { v4 as uuidv4 } from 'uuid';
import { logger } from '../../utils/logger';
import { ImageProcessingError } from '../../utils/errors';

interface ImageSize {
  width: number;
  height: number;
  suffix: string;
}

const IMAGE_SIZES: ImageSize[] = [
  { width: 800, height: 800, suffix: 'large' },
  { width: 400, height: 400, suffix: 'medium' },
  { width: 200, height: 200, suffix: 'thumbnail' }
];

export class ImageStorageService {
  private readonly bucketName = 'candidates';
  private readonly maxFileSize = 5 * 1024 * 1024; // 5MB
  private readonly allowedMimeTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  /**
   * Traite et stocke une image avec plusieurs tailles
   */
  async uploadContestantImage(file: Buffer | File, mimeType: string): Promise<string> {
    try {
      // Vérifier si on est dans un navigateur
      const isBrowser = typeof window !== 'undefined';
      
      // Dans le navigateur, on utilise directement le fichier avec Supabase
      if (isBrowser && file instanceof File) {
        // Validation du type de fichier
        if (!this.allowedMimeTypes.includes(file.type)) {
          throw new ImageProcessingError(
            'INVALID_TYPE',
            'Format d\'image non supporté. Utilisez JPG, PNG ou WebP'
          );
        }

        // Validation de la taille
        if (file.size > this.maxFileSize) {
          throw new ImageProcessingError(
            'FILE_TOO_LARGE',
            'L\'image ne doit pas dépasser 5MB'
          );
        }
        
        const fileName = `${uuidv4()}.webp`;
        
        // Télécharger directement le fichier sans traitement
        // Upload du fichier
        const uploadResult = await supabase.storage
          .from(this.bucketName)
          .upload(fileName, file, {
            contentType: file.type,
            cacheControl: '31536000', // Cache 1 an
            upsert: true
          });

        if (uploadResult.error) {
          throw new Error(`Erreur lors de l'upload de l'image: ${uploadResult.error.message}`);
        }

        // Récupérer l'URL publique
        const publicUrlResult = supabase.storage
          .from(this.bucketName)
          .getPublicUrl(fileName);

        if (publicUrlResult.data && publicUrlResult.data.publicUrl) {
          return publicUrlResult.data.publicUrl;
        } else {
          throw new Error('Impossible de récupérer l\'URL publique de l\'image');
        }
      } else {
        // Code côté serveur existant
        if (!Buffer.isBuffer(file)) {
          throw new ImageProcessingError('INVALID_FILE', 'Fichier invalide');
        }
        
        // Validation du type de fichier
        if (!this.allowedMimeTypes.includes(mimeType)) {
          throw new ImageProcessingError(
            'INVALID_TYPE',
            'Format d\'image non supporté. Utilisez JPG, PNG ou WebP'
          );
        }

        // Validation de la taille
        if (file.length > this.maxFileSize) {
          throw new ImageProcessingError(
            'FILE_TOO_LARGE',
            'L\'image ne doit pas dépasser 5MB'
          );
        }

        const imageId = uuidv4();
        const urls: string[] = [];

        // Traitement et upload pour chaque taille
        for (const size of IMAGE_SIZES) {
          const processedImage = await this.processImage(file, size);
          const fileName = `${imageId}_${size.suffix}.webp`;
          
          // Upload de l'image traitée
          const uploadResult = await supabase.storage
            .from(this.bucketName)
            .upload(fileName, processedImage, {
              contentType: 'image/webp',
              cacheControl: '31536000', // Cache 1 an
              upsert: false
            });
          
          if (uploadResult.error) {
            throw new Error(`Erreur lors de l'upload de l'image: ${uploadResult.error.message}`);
          }

          // Récupération de l'URL publique
          const publicUrlResult = supabase.storage
            .from(this.bucketName)
            .getPublicUrl(fileName);
          
          if (publicUrlResult.data && publicUrlResult.data.publicUrl) {
            urls.push(publicUrlResult.data.publicUrl);
          } else {
            throw new Error('Impossible de récupérer l\'URL publique de l\'image');
          }
        }

        // Retourner l'URL de l'image principale
        return urls[0];
      }
    } catch (error) {
      logger.error('Image upload error:', error);
      throw this.handleImageError(error);
    }
  }

  /**
   * Supprime toutes les versions d'une image
   */
  async deleteContestantImage(imageUrl: string): Promise<void> {
    try {
      const fileName = this.extractFileNameFromUrl(imageUrl);
      const baseFileName = fileName.split('_')[0];

      const filesToDelete = IMAGE_SIZES.map(
        size => `${baseFileName}_${size.suffix}.webp`
      );

      const removeResult = await supabase.storage
        .from(this.bucketName)
        .remove(filesToDelete);

      if (removeResult.error) {
        throw new Error(`Erreur lors de la suppression des images: ${removeResult.error.message}`);
      }
    } catch (error) {
      logger.error('Image deletion error:', error);
      throw this.handleImageError(error);
    }
  }

  /**
   * Traite une image pour une taille donnée
   */
  private async processImage(
    file: Buffer,
    size: ImageSize
  ): Promise<Buffer> {
    // Vérifier si nous sommes dans un environnement navigateur
    const isBrowser = typeof window !== 'undefined';
    
    try {
      if (isBrowser) {
        // En environnement navigateur, retourner simplement le fichier original
        logger.info('Traitement d\'image désactivé dans le navigateur');
        return file;
      }
      
      // Vérifier que sharp est disponible
      if (!sharp) {
        throw new Error('Sharp n\'est pas disponible pour le traitement d\'image');
      }
      
      // En environnement serveur, traiter l'image normalement
      const sharpInstance = sharp(file);
      return await sharpInstance
        .resize(size.width, size.height, {
          fit: 'cover',
          position: 'centre' as const
        })
        .webp({ quality: 80 })
        .toBuffer();
    } catch (error) {
      logger.error('Image processing error:', error);
      throw new ImageProcessingError(
        'PROCESSING_ERROR',
        'Erreur lors du traitement de l\'image'
      );
    }
  }

  /**
   * Extrait le nom du fichier depuis l'URL
   */
  private extractFileNameFromUrl(url: string): string {
    if (!url) return '';
    const urlParts = url.split('/');
    return urlParts[urlParts.length - 1] || '';
  }

  /**
   * Gère les erreurs d'image
   */
  private handleImageError(error: unknown): Error {
    // Si c'est déjà une ImageProcessingError, on la retourne telle quelle
    if (error instanceof ImageProcessingError) {
      return error;
    }
    
    // Gestion des erreurs de Supabase ou autres erreurs avec propriété message
    if (error && typeof error === 'object' && 'message' in error) {
      const errorMessage = typeof error.message === 'string' 
        ? error.message 
        : 'Erreur lors du téléchargement de l\'image';
      
      // Vérifier les erreurs connues de Supabase
      if (errorMessage.includes('Bucket not found')) {
        return new ImageProcessingError('STORAGE_ERROR', 'Erreur de stockage: Bucket non trouvé');
      }
      
      if (errorMessage.includes('Invalid image')) {
        return new ImageProcessingError('INVALID_IMAGE', 'Image invalide ou corrompue');
      }
      
      if (errorMessage.includes('File size exceeds')) {
        return new ImageProcessingError('FILE_TOO_LARGE', 'La taille du fichier dépasse la limite autorisée');
      }
      
      // Erreur générique avec le message d'origine
      return new ImageProcessingError('UPLOAD_ERROR', errorMessage);
    }

    // Gestion des erreurs standard
    if (error instanceof Error) {
      // Si c'est une erreur réseau
      if ('code' in error && (error as any).code === 'ECONNABORTED') {
        return new ImageProcessingError('NETWORK_ERROR', 'Erreur de connexion au serveur');
      }
      
      // Autres erreurs inattendues
      return new ImageProcessingError(
        'UNKNOWN_ERROR',
        `Erreur inattendue: ${error.message || 'Aucun détail disponible'}`
      );
    }

    // Cas de secours pour tout type d'erreur inconnu
    return new ImageProcessingError(
      'UNKNOWN_ERROR',
      'Une erreur inattendue est survenue lors du traitement de l\'image'
    );
  }
}